import { formatRelativeTime } from "Utils/formatTime";
import { getMinorSafeDisplayName } from "Utils/minorSafety";

const STYLE_TO_VARIANT = {
  PRIMARY: "primary",
  SUCCESS: "success",
  DESTRUCTIVE: "danger",
  DANGER: "danger",
  WARNING: "warning",
};

const LABEL_TO_VARIANT = {
  accept: "success",
  approve: "success",
  confirm: "success",
  decline: "danger",
  dismiss: "danger",
  resend: "warning",
};

const COMMON_HTML_ENTITIES = {
  "&amp;": "&",
  "&quot;": '"',
  "&#39;": "'",
  "&apos;": "'",
  "&nbsp;": " ",
};

const TYPE_MESSAGE_FACTORIES = {
  SOCIAL__NEW_FOLLOWER: ({ followerName } = {}) =>
    followerName
      ? `${followerName} started following you.`
      : "Someone started following you.",

  SOCIAL__NEW_CONNECTION: ({ partnerName } = {}) =>
    partnerName
      ? `${partnerName} sent you a connection request.`
      : "You have a new connection request.",

  ADMIN__SUBJECT_SENT_FOR_APPROVAL: ({
    submittedByUserName,
    subjectTitle,
  } = {}) =>
    submittedByUserName && subjectTitle
      ? `${submittedByUserName} submitted "${subjectTitle}" for approval.`
      : "A subject was submitted for approval.",

  ACCOUNT__PARENT_CONSENT: ({ childFullName, parentEmail } = {}) => {
    if (childFullName) return `Parental consent is required for ${childFullName}.`;
    if (parentEmail) return `Verification email sent to ${parentEmail}.`;
    return "Complete your account setup.";
  },

  BILLING__SUBSCRIPTION_PURCHASED: ({ planName, amount, currency } = {}) => {
    if (planName && amount) return `${planName} - ${currency ?? ""} ${amount}`.trim();
    if (planName) return `Your ${planName} subscription was updated.`;
    return "Subscription update.";
  },
};

const readText = (value) => String(value ?? "").trim();

const normalizeList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.requests)) return payload.requests;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.list)) return payload.list;
  if (Array.isArray(payload?.connections)) return payload.connections;
  return [];
};

const decodeHtmlEntities = (value) => {
  let nextValue = value;

  Object.entries(COMMON_HTML_ENTITIES).forEach(([entity, replacement]) => {
    nextValue = nextValue.replaceAll(entity, replacement);
  });

  return nextValue;
};

const stripHtml = (value) =>
  decodeHtmlEntities(readText(value))
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const toKeywordBlob = (...values) =>
  values
    .flat(Infinity)
    .map((value) => stripHtml(value))
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const resolveVariant = (style, label) =>
  LABEL_TO_VARIANT[label?.toLowerCase()] ??
  STYLE_TO_VARIANT[style] ??
  "primary";

const PROFILE_ROUTE_PREFIX = "/my-network/";

const PROFILE_IDENTIFIER_QUERY_KEYS = [
  "username",
  "userName",
  "preferredUsername",
  "preferred_username",
  "userId",
  "userID",
  "profileId",
  "profileID",
  "id",
];

const PROFILE_PATH_MARKERS = [
  "my-network",
  "profile",
  "profiles",
  "user-profile",
  "users",
  "user",
  "members",
  "member",
];

const PROFILE_PATH_IGNORED_SEGMENTS = new Set([
  "api",
  "ks",
  "sns",
  "v1",
  "v2",
  "profile",
  "profiles",
  "user-profile",
  "users",
  "user",
  "members",
  "member",
  "view",
]);

const isViewProfileAction = (label) => /\bview\s+profile\b/i.test(label);

const buildMyNetworkProfileRoute = (identifier) => {
  const nextIdentifier = readText(identifier);
  if (!nextIdentifier) return "";

  return `${PROFILE_ROUTE_PREFIX}${encodeURIComponent(nextIdentifier)}`;
};

const findProfileIdentifierFromSearch = (searchParams) => {
  for (const key of PROFILE_IDENTIFIER_QUERY_KEYS) {
    const value = readText(searchParams.get(key));
    if (value) return value;
  }

  return "";
};

const findProfileIdentifierFromPath = (pathname) => {
  const segments = pathname.split("/").filter(Boolean);

  for (const marker of PROFILE_PATH_MARKERS) {
    const index = segments.findIndex(
      (segment) => segment.toLowerCase() === marker,
    );
    const candidate = segments[index + 1];

    if (
      index >= 0 &&
      candidate &&
      !PROFILE_PATH_IGNORED_SEGMENTS.has(candidate.toLowerCase())
    ) {
      return candidate;
    }
  }

  return "";
};

const normalizeProfileActionUrl = (url) => {
  const nextUrl = readText(url);
  if (!nextUrl) return "";

  try {
    const parsedUrl = new URL(nextUrl, "https://kewordal.local");
    const pathname = parsedUrl.pathname.replace(/\/+$/, "");

    if (pathname.startsWith(PROFILE_ROUTE_PREFIX)) {
      return `${pathname}${parsedUrl.search}${parsedUrl.hash}`;
    }

    const identifier =
      findProfileIdentifierFromSearch(parsedUrl.searchParams) ||
      findProfileIdentifierFromPath(pathname);

    return buildMyNetworkProfileRoute(identifier) || nextUrl;
  } catch {
    return nextUrl;
  }
};

const extractChallengeIdFromUrl = (url) => {
  const nextUrl = readText(url);
  if (!nextUrl) return "";

  try {
    const parsedUrl = new URL(nextUrl, "https://kewordal.local");
    const challengeId =
      parsedUrl.searchParams.get("challengeId") ||
      parsedUrl.searchParams.get("id");
    if (challengeId) return challengeId;

    const segments = parsedUrl.pathname.split("/").filter(Boolean);
    for (const marker of ["challenge-details", "challenges", "challenge"]) {
      const index = segments.findIndex(
        (segment) => segment.toLowerCase() === marker,
      );
      if (index >= 0 && segments[index + 1]) {
        return segments[index + 1];
      }
    }

    return "";
  } catch {
    return "";
  }
};

const normalizeChallengeActionUrl = (url) => {
  const challengeId = extractChallengeIdFromUrl(url);
  return challengeId
    ? `/challenge-hub?challengeId=${encodeURIComponent(challengeId)}`
    : "/challenge-hub";
};

const resolveChallengeIdFromSource = (source = {}) => {
  const target = source || {};

  return readText(
    target.challengeId ||
      target.challenge_id ||
      target.challenge?.challengeId ||
      target.challenge?.challenge_id ||
      target.challenge?.id ||
      target.challenge?.uuid,
  );
};

const resolveNotificationChallengeId = (notification = {}) => {
  const source = notification || {};
  const metadata = source.metadata || {};
  const data = source.data || {};
  const actions = Array.isArray(source.actions) ? source.actions : [];
  const directId = [
    resolveChallengeIdFromSource(source),
    resolveChallengeIdFromSource(metadata),
    resolveChallengeIdFromSource(data),
  ].find(Boolean);

  if (directId) return directId;

  return [
    source.actionLink,
    source.url,
    source.link,
    source.redirectUrl,
    metadata.actionLink,
    metadata.url,
    metadata.link,
    data.actionLink,
    data.url,
    data.link,
    ...actions.map((action) => action?.url || action?.href || action?.link),
  ]
    .map(extractChallengeIdFromUrl)
    .find(Boolean) || "";
};

const resolveActor = (notification = {}) =>
  notification.sender ||
  notification.actor ||
  notification.user ||
  notification.producer ||
  notification.createdBy ||
  {};

const pickUserFromConnectionRequest = (request = {}) => {
  const source = request || {};

  return (
    source.requester ||
    source.fromUser ||
    source.sender ||
    source.user ||
    source.partner ||
    source.connectedUser ||
    {}
  );
};

const collectIdentityValues = (source = {}) => {
  const target = source || {};

  return [
    target.id,
    target.userId,
    target.user_id,
    target.uuid,
    target.publicId,
    target.public_id,
    target.senderId,
    target.requesterId,
    target.fromUserId,
    target.partnerId,
    target.username,
    target.userName,
    target.preferredUsername,
    target.preferred_username,
  ]
    .map(readText)
    .filter(Boolean);
};

const resolveConnectionRequestId = (request = {}) => {
  const source = request || {};

  return readText(
    source.requestId ||
      source.connectionRequestId ||
      source.connectionRelationshipUuid ||
      source.id ||
      source.uuid,
  );
};

const resolveNotificationRequestId = (notification = {}) => {
  const source = notification || {};
  const metadata = source.metadata || {};
  const data = source.data || {};

  return readText(
    source.requestId ||
      source.connectionRequestId ||
      source.connectionRelationshipUuid ||
      metadata.requestId ||
      metadata.connectionRequestId ||
      metadata.connectionRelationshipUuid ||
      data.requestId ||
      data.connectionRequestId ||
      data.connectionRelationshipUuid,
  );
};

const getConnectionRequestStatus = (request = {}) =>
  readText((request || {}).status || (request || {}).requestStatus).toUpperCase();

const isPendingConnectionRequest = (request = {}) => {
  const status = getConnectionRequestStatus(request);
  return !status || status === "PENDING";
};

const resolveNotificationActorIds = (notification = {}) => {
  const actor = resolveActor(notification);
  const metadata = notification.metadata || {};
  const data = notification.data || {};

  return [
    ...collectIdentityValues(actor),
    ...collectIdentityValues(metadata),
    ...collectIdentityValues(data),
    readText(notification.senderId),
    readText(notification.producerId),
  ].filter(Boolean);
};

const resolveNotificationActorId = (notification = {}) =>
  resolveNotificationActorIds(notification).find((id) => /^\d+$/.test(id)) ||
  resolveNotificationActorIds(notification)[0] ||
  "";

const normalizeDisplayName = (value) =>
  readText(value).replace(/\s+/g, " ").toLowerCase();

const getDisplayNameValues = (source = {}) => {
  const target = source || {};

  return [
    getMinorSafeDisplayName(target),
    target.fullName,
    target.displayName,
    target.name,
    target.username,
    target.userName,
    target.preferredUsername,
    target.preferred_username,
    target.handle,
    target.followerName,
    target.senderName,
    target.requesterName,
    target.fromUserName,
  ]
    .map(readText)
    .filter(Boolean);
};

const extractActorNameFromMessage = (message = "") => {
  const match = stripHtml(message).match(
    /^(.+?)\s+(?:is now following you|started following you|sent you (?:a )?connection request|challenged you\b)/i,
  );

  return readText(match?.[1]);
};

const isFollowerNotification = (notification = {}, message = "") => {
  const type = readText(notification.type || notification.category).toUpperCase();
  const keywords = toKeywordBlob(
    type,
    message,
    notification.title,
    notification.subject,
    notification.metadata,
    notification.data,
  );

  return /new_follower|started following|is now following|following you/.test(
    keywords,
  );
};

const doesFollowingListContainUser = (following = [], targetUserId) => {
  const target = readText(targetUserId);
  if (!target) return false;

  return normalizeList(following).some((record) => {
    const followee =
      record?.followee ||
      record?.followeeUser ||
      record?.following ||
      record?.toUser ||
      record?.targetUser ||
      record?.user ||
      record;
    const ids = [
      ...collectIdentityValues(record),
      ...collectIdentityValues(followee),
      readText(record?.followeeId),
      readText(record?.toUserId),
      readText(record?.targetUserId),
    ];

    return ids.some((id) => id === target);
  });
};

const findMatchingConnectionRequest = (
  notification = {},
  receivedConnectionRequests = [],
) => {
  const directRequestId = resolveNotificationRequestId(notification);
  const requests = normalizeList(receivedConnectionRequests);

  if (!requests.length) return null;

  if (directRequestId) {
    const byRequestId = requests.find(
      (request) =>
        isPendingConnectionRequest(request) &&
        resolveConnectionRequestId(request) === directRequestId,
    );
    if (byRequestId) return byRequestId;
  }

  const actorIds = new Set(resolveNotificationActorIds(notification));
  if (actorIds.size) {
    const byActorId = requests.find((request) => {
      if (!isPendingConnectionRequest(request)) return false;

      const requester = pickUserFromConnectionRequest(request);
      const requestIds = [
        ...collectIdentityValues(request),
        ...collectIdentityValues(requester),
      ];

      return requestIds.some((id) => actorIds.has(id));
    });

    if (byActorId) return byActorId;
  }

  const actorName = normalizeDisplayName(resolveActorName(notification));
  if (!actorName) return null;

  return (
    requests.find((request) => {
      if (!isPendingConnectionRequest(request)) return false;

      const requester = pickUserFromConnectionRequest(request);
      const requestNames = [
        ...getDisplayNameValues(requester),
        ...getDisplayNameValues(request),
      ].map(normalizeDisplayName);

      return requestNames.some((requestName) => requestName === actorName);
    }) || null
  );
};

const resolveActorName = (notification = {}) => {
  const actor = resolveActor(notification);
  const metadata = notification.metadata || {};
  const messageActorName = extractActorNameFromMessage(
    resolveMessage(notification),
  );

  return (
    getDisplayNameValues(actor)[0] ||
    readText(metadata.followerName) ||
    readText(metadata.partnerName) ||
    readText(metadata.submittedByUserName) ||
    readText(notification.senderName) ||
    messageActorName
  );
};

const resolveConnectionRequesterName = (request = {}) => {
  const requester = pickUserFromConnectionRequest(request);

  return getDisplayNameValues(requester)[0] || getDisplayNameValues(request)[0];
};

const resolveConnectionRequesterId = (request = {}) => {
  const source = request || {};
  const requester = pickUserFromConnectionRequest(source);

  return (
    collectIdentityValues(requester)[0] ||
    readText(
      source.fromUserId ||
        source.requesterId ||
        source.senderId ||
        source.partnerId,
    )
  );
};

const resolveSeed = (notification = {}) =>
  resolveActorName(notification) ||
  readText(notification.data?.title) ||
  readText(notification.title) ||
  readText(notification.subject) ||
  String(notification.id ?? "");

const resolveMedia = (notification = {}) => {
  const actor = resolveActor(notification);
  const media = notification.media || notification.data?.media;
  const mediaUrl =
    readText(media?.mediaUrl) ||
    readText(media?.url) ||
    readText(notification.mediaUrl);
  const isAvatar = media?.metadata?.category === "AVATAR";
  const actorAvatar =
    readText(actor.avatarURL) ||
    readText(actor.avatarUrl) ||
    readText(actor.avatar) ||
    readText(actor.profileImageUrl) ||
    readText(notification.avatarURL) ||
    readText(notification.avatarUrl);
  let nextIcon = null;

  if (!actorAvatar && !isAvatar && mediaUrl) {
    nextIcon = mediaUrl;
  }

  return {
    avatar: actorAvatar || (isAvatar ? mediaUrl : null),
    icon: nextIcon,
    mediaAlt: readText(media?.metadata?.altText) || resolveActorName(notification),
  };
};

const resolveMessage = (notification = {}) => {
  const fallbackMessage =
    TYPE_MESSAGE_FACTORIES[notification.type]?.(notification.metadata) || "";

  return stripHtml(
    notification.body ||
      notification.content ||
      notification.text ||
      notification.data?.body ||
      notification.data?.content ||
      notification.title ||
      notification.subject ||
      fallbackMessage,
  );
};

const isConnectionRequestNotification = (
  notification = {},
  pendingConnectionRequest = null,
  message = "",
) => {
  if (pendingConnectionRequest) return true;

  const type = readText(notification.type || notification.category).toUpperCase();
  const keywords = toKeywordBlob(
    type,
    message,
    notification.title,
    notification.subject,
    notification.metadata,
    notification.data,
  );

  return (
    Boolean(resolveNotificationRequestId(notification)) &&
    /connection|request|follow/.test(keywords)
  );
};

const isChallengeRequestNotification = (
  notification = {},
  message = "",
  challengeId = "",
) => {
  if (!challengeId) return false;

  const type = readText(notification.type || notification.category).toUpperCase();
  const keywords = toKeywordBlob(
    type,
    message,
    notification.title,
    notification.subject,
    notification.metadata,
    notification.data,
  );
  const actionKeywords = toKeywordBlob(
    (notification.actions || []).map((action) => action?.label),
  );
  const actionUrlKeywords = toKeywordBlob(
    notification.actionLink,
    notification.url,
    (notification.actions || []).map(
      (action) => action?.url || action?.href || action?.link,
    ),
  );
  const isChallenge = /challenge|challenged|quiz battle/.test(
    `${keywords} ${actionUrlKeywords}`,
  );

  if (!isChallenge) return false;

  return (
    /challenged you|sent you (?:a )?challenge|received (?:a )?challenge|challenge (?:request|received|invite|invitation)|(?:request|received|invite|invitation).*challenge/.test(
      keywords,
    ) ||
    (type.includes("CHALLENGE") && /REQUEST|RECEIVED|INVIT|PENDING/.test(type)) ||
    (/play (?:now|game)/.test(actionKeywords) && /decline|dismiss/.test(actionKeywords))
  );
};

const resolveTab = (notification, message) => {
  const type = readText(notification.type || notification.category).toUpperCase();
  const keywords = toKeywordBlob(type, message, notification.title, notification.subject);

  if (
    type.startsWith("SOCIAL__") ||
    /follow|friend|network|connection|challenge|invite|mentor/.test(keywords)
  ) {
    return "network";
  }

  if (
    /post|comment|reply|mention|share|like|reaction|keyword|list/.test(keywords)
  ) {
    return "posts";
  }

  return "general";
};

const resolveKind = (notification, message, seed) => {
  const type = readText(notification.type || notification.category).toUpperCase();
  const keywords = toKeywordBlob(type, message, seed, notification.title, notification.subject);

  if (/verify|warning|security|alert|consent|password|email/.test(keywords)) {
    return "warning";
  }

  if (/congrats|created|approved|complete|subscription|success/.test(keywords)) {
    return "success";
  }

  if (/challenge|quiz|battle|trophy/.test(keywords)) {
    return "challenge";
  }

  if (/school|college|university|academy|institution/.test(keywords)) {
    return "institution";
  }

  if (/post|comment|reply|mention|share|like|reaction|keyword|list/.test(keywords)) {
    return "post";
  }

  if (
    type.startsWith("SOCIAL__") ||
    /follow|friend|network|connection|invite|mentor|teacher|learner/.test(keywords)
  ) {
    return "network";
  }

  return "general";
};

const resolveIsRequest = (notification, message, actions = []) => {
  const type = readText(notification.type || notification.category).toUpperCase();
  const keywords = toKeywordBlob(
    type,
    message,
    notification.title,
    notification.subject,
  );

  return (
    actions.length > 0 ||
    /request|challenge|invite|invitation|follow back/.test(keywords)
  );
};

const adaptAction = ({ label, url, method = "GET", style } = {}) => {
  const nextLabel = stripHtml(label);
  if (!nextLabel) return null;

  const lowerLabel = nextLabel.toLowerCase();
  const nextUrl = isViewProfileAction(nextLabel)
    ? normalizeProfileActionUrl(url)
    : lowerLabel.includes("challenge")
      ? normalizeChallengeActionUrl(url)
      : readText(url);

  return {
    label: nextLabel,
    url: nextUrl,
    method,
    variant: resolveVariant(style, nextLabel),
  };
};

const buildConnectionRequestActions = (requestId, targetUserId = "") => {
  if (!requestId) return [];

  const url = `/sns/v1/connections/requests/${encodeURIComponent(requestId)}`;

  return [
    {
      label: "Accept",
      url,
      method: "PATCH",
      variant: "primary",
      body: { status: "APPROVED" },
      networkAction: "accept-connection",
      targetUserId,
    },
    {
      label: "Decline",
      url,
      method: "PATCH",
      variant: "neutral",
      body: { status: "DECLINED" },
    },
  ];
};

const buildChallengeRequestActions = (challengeId) => {
  if (!challengeId) return [];

  const encodedChallengeId = encodeURIComponent(challengeId);

  return [
    {
      label: "Play Now",
      url: `/challenge-hub?challengeId=${encodedChallengeId}`,
      method: "GET",
      variant: "success",
    },
    {
      label: "View",
      url: `/challenge-hub/challenge-details/${encodedChallengeId}`,
      method: "GET",
      variant: "neutral",
    },
  ];
};

const buildFollowerActions = ({
  targetUserId,
  isFollowingBack = false,
  profileUrl = "",
}) => {
  if (!targetUserId) return [];

  return [
    {
      label: isFollowingBack ? "Unfollow" : "Follow Back",
      method: isFollowingBack ? "DELETE" : "POST",
      variant: isFollowingBack ? "neutral" : "primary",
      networkAction: isFollowingBack ? "unfollow" : "follow-back",
      targetUserId,
    },
    ...(profileUrl
      ? [
          {
            label: "View Profile",
            url: profileUrl,
            method: "GET",
            variant: "primary",
          },
        ]
      : []),
  ];
};

export const adaptNotification = (notification, context = {}) => {
  const hasConnectionRequestContext =
    context.receivedConnectionRequests !== undefined;
  const pendingConnectionRequest = findMatchingConnectionRequest(
    notification,
    context.receivedConnectionRequests,
  );
  const baseMessage = resolveMessage(notification);
  const connectionRequestId =
    resolveConnectionRequestId(pendingConnectionRequest) ||
    resolveNotificationRequestId(notification);
  const connectionRequesterName = resolveConnectionRequesterName(
    pendingConnectionRequest,
  );
  const connectionRequesterId =
    resolveConnectionRequesterId(pendingConnectionRequest) ||
    resolveNotificationActorId(notification);
  const challengeId = resolveNotificationChallengeId(notification);
  const followerTargetUserId = resolveNotificationActorId(notification);
  const isConnectionRequest =
    Boolean(pendingConnectionRequest) ||
    (!hasConnectionRequestContext &&
      isConnectionRequestNotification(
        notification,
        pendingConnectionRequest,
        baseMessage,
      ));
  const isChallengeRequest =
    !isConnectionRequest &&
    isChallengeRequestNotification(notification, baseMessage, challengeId);
  const isFollower = !isConnectionRequest && isFollowerNotification(notification, baseMessage);
  const followerProfileUrl = normalizeProfileActionUrl(
    notification.actionLink || notification.url || notification.data?.url || "",
  );
  const message =
    isConnectionRequest && connectionRequestId
      ? `${
          connectionRequesterName || resolveActorName(notification) || "Someone"
        } sent you a connection request.`
      : baseMessage;
  const seed = resolveSeed(notification);
  const kind = resolveKind(notification, message, seed);
  const actions =
    isConnectionRequest && connectionRequestId
      ? buildConnectionRequestActions(connectionRequestId, connectionRequesterId)
      : isChallengeRequest
        ? buildChallengeRequestActions(challengeId)
        : isFollower && followerTargetUserId
        ? buildFollowerActions({
            targetUserId: followerTargetUserId,
            isFollowingBack: doesFollowingListContainUser(
              context.following,
              followerTargetUserId,
            ),
            profileUrl: followerProfileUrl,
          })
        : (notification.actions ?? []).map(adaptAction).filter(Boolean);
  const isActionRequired =
    isConnectionRequest ||
    isChallengeRequest ||
    actions.some((action) => action.method && action.method !== "GET");

  return {
    id: notification.id,
    type: notification.type || notification.category || "general",
    tab: resolveTab(notification, message),
    kind,
    isRequest: resolveIsRequest(notification, message, actions),
    isActionRequired,
    seed,
    actorName: resolveActorName(notification),
    message,
    time: formatRelativeTime(notification.createdAt),
    createdAt: notification.createdAt || notification.timestamp || null,
    isRead: notification.isRead,
    actionLink: notification.actionLink ?? null,
    actions,
    ...resolveMedia(notification),
  };
};
